/**
 * AiBadge — tiny "made by AI" chip used wherever a piece of UI content
 * was generated or proposed by Ando (AI-pre-compiled tasks, AI overview
 * paragraphs, generated drafts, …).
 *
 * Brand-aligned: Sparkles glyph + ando-orange tint, matching the
 * existing usage at ``DraftActionBar``, ``MarpDeckEditor``,
 * ``ActionDrawerReasoning``. Centralised here so every surface gets
 * the same affordance — no copy-paste of the chip layout.
 *
 * Two sizes: ``sm`` (table rows, list items — 11px text) and ``md``
 * (card / panel headers — 12px text). The aria-label is always
 * present so screen readers announce "Generato da Ando AI" regardless
 * of whether the optional label is shown.
 */

import { Sparkles } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";

export type AiBadgeSize = "sm" | "md";

export interface AiBadgeProps {
  /** Optional visible label — defaults to "AI" (sm) / "Generato da Ando" (md). */
  readonly label?: React.ReactNode;
  /** Visual size — drives padding, icon size, font size. */
  readonly size?: AiBadgeSize;
  /** Optional aria-label override (still announced when label is visible). */
  readonly "aria-label"?: string;
  readonly className?: string;
  /** Render as a button when clickable (e.g. opens an "about AI proposals" popover). */
  readonly onClick?: () => void;
}

const SIZE_TOKENS: Record<
  AiBadgeSize,
  {
    container: string;
    icon: string;
    text: string;
  }
> = {
  sm: {
    container: "h-5 gap-1 px-1.5 text-[10px] leading-none",
    icon: "h-3 w-3",
    text: "font-medium tracking-wide uppercase",
  },
  md: {
    container: "h-6 gap-1.5 px-2 text-[11px] leading-none",
    icon: "h-3.5 w-3.5",
    text: "font-medium",
  },
};

export const AiBadge: React.FC<AiBadgeProps> = ({
  label,
  size = "sm",
  className,
  onClick,
  "aria-label": ariaLabelProp,
}) => {
  const tokens = SIZE_TOKENS[size];
  const visibleLabel = label ?? (size === "sm" ? "AI" : "Generato da Ando");
  const ariaLabel = ariaLabelProp ?? "Generato da Ando AI";

  const inner = (
    <>
      <Sparkles
        className={cn("shrink-0 text-ando-orange", tokens.icon)}
        aria-hidden
        strokeWidth={2}
      />
      <span className={tokens.text}>{visibleLabel}</span>
    </>
  );

  const baseClass = cn(
    "inline-flex items-center rounded-full",
    "border border-ando-orange/15 bg-ando-orange-soft text-ando-orange",
    tokens.container,
    onClick && "cursor-pointer transition-colors hover:bg-ando-orange/15",
    className,
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={baseClass}
      >
        {inner}
      </button>
    );
  }
  // Static (non-clickable) badge needs ``role="img"`` so screen readers
  // honour the ``aria-label`` — a bare ``<span>`` with an aria-label has
  // no implicit role and SRs typically skip it.
  return (
    <span role="img" aria-label={ariaLabel} className={baseClass}>
      {inner}
    </span>
  );
};

AiBadge.displayName = "AiBadge";
